// 15.C — «Спросить специалиста» (платная консультация живого эксперта, RFQ).
// Единый композитор: заказчик прикрепляет свои замечания (контекст сохраняется
// точь-в-точь) + пишет вопрос → одна заявка со списком вложений уходит координатору.
// Не в каждой находке (навязчиво), а два ненавязчивых входа: баннер «Готовность»
// и окно «Замечания». Бэк: POST /projects/{id}/consultations { question, attachments }.
import { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { MessageCircleQuestion, CheckCircle2 } from 'lucide-react';
import { api, ApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { ConsultationThreadModal } from '@/components/ConsultationThreadModal';
import type { RemarksDashboardResponse, Remark } from '@/types/api';

interface Attachment {
  kind: string;
  title?: string;
  ref?: string;
  norm?: string;
  verdict?: string;
  citation?: string;
  document_title?: string;
  page?: number;
}

function remarkToAttachment(r: Remark): Attachment {
  return {
    kind: 'remark',
    title: (r.body || '').slice(0, 900),
    ref: r.section_code || undefined,
    norm: (r.cited_norms && r.cited_norms.length) ? r.cited_norms.join(', ') : undefined,
    verdict: r.severity || undefined,
  };
}

export function AskSpecialistModal({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const [question, setQuestion] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sent, setSent] = useState(false);
  const [newId, setNewId] = useState<string | null>(null); // id созданной консультации
  const [threadOpen, setThreadOpen] = useState(false);

  const remarksQ = useQuery<RemarksDashboardResponse>({
    queryKey: ['remarks', projectId, { includeArchived: false }],
    queryFn: () => api.get<RemarksDashboardResponse>(`/projects/${projectId}/remarks/dashboard?include_archived=false`),
  });
  // Только незакрытые/незаархивированные — их и осмысленно нести эксперту.
  const remarks = useMemo(
    () => (remarksQ.data?.remarks || []).filter((r) => r.status !== 'archived'),
    [remarksQ.data],
  );

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const attachments = useMemo(
    () => remarks.filter((r) => selected.has(r.id)).map(remarkToAttachment),
    [remarks, selected],
  );

  const submit = useMutation({
    mutationFn: () => api.post<{ id: string; status: string }>(`/projects/${projectId}/consultations`, {
      question: question.trim(),
      attachments,
    }),
    onSuccess: (r) => { setSent(true); setNewId(r.id); },
  });

  const errText = submit.isError
    ? ((submit.error as ApiError | undefined)?.status === 401 ? 'Требуется вход в систему.' : 'Не удалось отправить запрос. Попробуйте ещё раз.')
    : null;

  if (threadOpen && newId) {
    return <ConsultationThreadModal projectId={projectId} consultationId={newId} onClose={onClose} />;
  }

  if (sent) {
    return (
      <Modal title="Вопрос отправлен" onClose={onClose} maxWidth={520}
        footer={
          <>
            <button type="button" onClick={onClose} className="rounded-pill text-muted" style={{ padding: '9px 18px', fontSize: 14, fontWeight: 600, background: '#fff', border: '1px solid #E5E7EB', cursor: 'pointer' }}>Закрыть</button>
            {newId && (
              <button type="button" onClick={() => setThreadOpen(true)} className="rounded-pill" style={{ padding: '9px 18px', fontSize: 14, fontWeight: 600, background: '#0A1628', color: '#fff', border: 'none', cursor: 'pointer' }}>Открыть переписку</button>
            )}
          </>
        }>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <CheckCircle2 size={22} style={{ color: '#059669', flexShrink: 0, marginTop: 2 }} />
          <div className="text-ink" style={{ fontSize: 14, lineHeight: 1.55 }}>
            Отправлено. Специалист ответит в переписке — следите за значком чата в шапке.
          </div>
        </div>
      </Modal>
    );
  }

  const canSend = question.trim().length > 0 && !submit.isPending;

  return (
    <Modal
      title="Спросить специалиста"
      onClose={onClose}
      maxWidth={560}
      footer={
        <div className="flex items-center justify-between" style={{ width: '100%' }}>
          <span className="text-muted" style={{ fontSize: 12.5 }}>
            Прикреплено: <b className="text-ink">{selected.size}</b>{selected.size ? ' · плюс ваш текст' : ''}
          </span>
          <div className="flex items-center" style={{ gap: 8 }}>
            <button type="button" onClick={onClose} className="rounded-pill text-muted" style={{ padding: '9px 18px', fontSize: 14, fontWeight: 600, background: '#fff', border: '1px solid #E5E7EB', cursor: 'pointer' }}>Отмена</button>
            <button type="button" onClick={() => canSend && submit.mutate()} disabled={!canSend} className="rounded-pill"
              style={{ padding: '9px 18px', fontSize: 14, fontWeight: 600, border: 'none',
                       background: canSend ? '#F59E0B' : '#F3F4F6', color: canSend ? '#1F1300' : '#9CA3AF',
                       cursor: canSend ? 'pointer' : 'not-allowed' }}>
              {submit.isPending ? 'Отправляем…' : 'Отправить вопрос'}
            </button>
          </div>
        </div>
      }
    >
      <p className="text-ink" style={{ fontSize: 13, lineHeight: 1.55, margin: '0 0 16px', background: 'rgba(245,158,11,.10)', border: '1px solid rgba(245,158,11,.35)', borderRadius: 8, padding: '10px 14px' }}>
        <MessageCircleQuestion size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
        Напишите вопрос — специалист ответит в переписке. Можно спросить и про систему, и по проекту. Если понадобится платная экспертная проработка, координатор оценит стоимость.
      </p>

      {/* Прикрепить из замечаний */}
      <div className="text-muted uppercase tracking-wider" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em', marginBottom: 8 }}>
        Прикрепить из ваших замечаний{remarks.length ? ` · ${remarks.length}` : ''}
      </div>
      {remarksQ.isLoading ? (
        <p className="text-muted" style={{ fontSize: 12.5 }}>Загрузка замечаний…</p>
      ) : remarks.length === 0 ? (
        <p className="text-muted" style={{ fontSize: 12.5, padding: '10px 12px', border: '1px dashed #E5E7EB', borderRadius: 8 }}>
          Пока нет замечаний для прикрепления. Можно задать вопрос просто текстом ниже.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto', paddingRight: 2 }}>
          {remarks.map((r) => {
            const on = selected.has(r.id);
            return (
              <label key={r.id} className="flex items-start"
                style={{ gap: 9, padding: '9px 11px', border: `1px solid ${on ? '#F59E0B' : '#F3F4F6'}`, borderRadius: 8, cursor: 'pointer', background: on ? 'rgba(245,158,11,.05)' : '#fff' }}>
                <input type="checkbox" checked={on} onChange={() => toggle(r.id)} style={{ marginTop: 3 }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="text-ink" style={{ fontSize: 12.5, lineHeight: 1.4, display: 'block' }}>
                    {(r.body || '').slice(0, 140)}{(r.body || '').length > 140 ? '…' : ''}
                  </span>
                  <span className="text-muted" style={{ fontSize: 11, marginTop: 2, display: 'block' }}>
                    {r.section_code ? `${r.section_code}` : ''}
                    {r.cited_norms && r.cited_norms.length ? `${r.section_code ? ' · ' : ''}${r.cited_norms.join(', ')}` : ''}
                    {r.severity ? `${(r.section_code || (r.cited_norms && r.cited_norms.length)) ? ' · ' : ''}${r.severity}` : ''}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      )}

      {/* Свободный вопрос */}
      <div className="text-muted uppercase tracking-wider" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em', margin: '18px 0 8px' }}>
        Ваш вопрос эксперту
      </div>
      <textarea
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        maxLength={8000}
        rows={4}
        placeholder="Опишите, что именно хотите уточнить у живого эксперта…"
        style={{ width: '100%', fontSize: 13, lineHeight: 1.5, padding: '11px 12px', border: '1px solid #CBD5E1', borderRadius: 8, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
      />
      {errText && <p style={{ color: '#B91C1C', fontSize: 12.5, marginTop: 8 }}>{errText}</p>}
    </Modal>
  );
}
